import { Box, FormControl, FormLabel, Select } from "@chakra-ui/react";
import { observer } from "mobx-react";
import Cask from "models/cask";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useHistory } from "react-router";
import { useStores } from "stores";
import { format, formatToString } from "utils/format";

const CaskSelector: FC<{ id: string }> = ({ id }) => {
  const { sessionStore, casksStore } = useStores();
  const { push } = useHistory();
  const [cask, setCask] = useState<Cask | undefined>(undefined);

  // Fetch casks on component mount
  useEffect(() => {
    if (!casksStore.isLoading && casksStore.casks.length === 0) {
      casksStore.fetchCasks();
    }
  }, [casksStore]);

  // Set initial cask based on id prop
  useEffect(() => {
    if (id && casksStore.casks.length > 0) {
      const initialCask = casksStore.casks.find((c) => c.id === parseInt(id));
      setCask(initialCask);
    }
  }, [id, casksStore.casks]);

  // Filter casks to only show those owned by the current user
  const ownedCasks = casksStore.casks.filter((c: Cask) =>
    c.caskUsers.some((cu) => cu.user.id === sessionStore.user?.id)
  );

  const handleCaskChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const caskId = parseInt(event.target.value);
    if (caskId) {
      const selectedCask = ownedCasks.find((c) => c.id === caskId);
      if (selectedCask) {
        setCask(selectedCask);
        push(`/label-editor/${selectedCask.id}`);
      }
    } else {
      // Handle case where no cask is selected
      setCask(undefined);
      push(`/label-editor`);
    }
  };

  if (casksStore.isLoading) {
    return (
      <FormControl>
        <FormLabel>
          <FormattedMessage
            id="labelEditor.selectCask"
            defaultMessage="Välj fat"
          />
        </FormLabel>
        <Select
          placeholder={formatToString("labelEditor.selectCask")}
          isDisabled
        />
      </FormControl>
    );
  }

  return (
    <FormControl isRequired>
      <FormLabel>
        <FormattedMessage
          id="labelEditor.selectCask"
          defaultMessage="Välj fat"
        />
      </FormLabel>
      <Select
        placeholder={formatToString("labelEditor.selectCask")}
        value={cask?.id || ""}
        onChange={handleCaskChange}
        size="lg"
      >
        {ownedCasks.map((c) => {
          const recipeInfo = c.recipe
            ? format(c.recipe.recipeInformation)
            : null;
          return (
            <option key={c.id} value={c.id}>
              {c.vismaCaskNumber || c.name} - {recipeInfo?.name || c.recipeText}
            </option>
          );
        })}
      </Select>
      {ownedCasks.length === 0 && (
        <Box color="gray.500" fontSize="sm" mt={2}>
          <FormattedMessage
            id="labelEditor.noCasks"
            defaultMessage="No casks available"
          />
        </Box>
      )}
    </FormControl>
  );
};

export default observer(CaskSelector);
